<script setup>
import { onMounted, ref, defineProps, defineEmits, defineExpose, watch, nextTick, onBeforeUnmount, computed } from 'vue';
import { useIcons } from '@/composables/icons.js';

const { User } = useIcons();

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  options: {
    type: Array,
    required: true
  },
  type: {
    type: String,
    default: 'text'
  },
  id: {
    type: String,
    required: true
  },
  placeholder: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: false
  },
  showProfilePicture: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  hideSearch: {
    type: Boolean,
    default: false
  },
  dataTestId: {
    type: String,
    required: false,
    default: ''
  },
  // Props para customização de cores
  bgClass: {
    type: String,
    default: 'bg-echoes-background-black'
  },
  borderClass: {
    type: String,
    default: 'border-echoes-border-gray'
  },
  hoverBgClass: {
    type: String,
    default: 'bg-echoes-background-black'
  },
  hoverBorderClass: {
    type: String,
    default: 'border-white/20'
  },
  hoverTextClass: {
    type: String,
    default: 'text-white'
  },
  focusBorderClass: {
    type: String,
    default: 'border-echoes-primary'
  },
  dropdownBgClass: {
    type: String,
    default: 'bg-echoes-background-black'
  },
  dropdownBorderClass: {
    type: String,
    default: 'border-echoes-border-gray'
  },
  optionHoverBgClass: {
    type: String,
    default: 'bg-echoes-background-gray/30'
  },
  avatarBgClass: {
    type: String,
    default: 'bg-echoes-primary'
  },
  size: {
    type: String,
    default: 'md'
  },
  truncated: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue']);
const input = ref(null);
const isOpen = ref(false);
const containerRef = ref(null);
const searchQuery = ref('');
const isDropdownUp = ref(false);

const displayValue = computed(() => {
  const selectedOption = props?.options?.find(option => option?.value === props?.modelValue);
  return selectedOption ? selectedOption?.displayValue || selectedOption?.label : props?.placeholder;
});

const filteredOptions = computed(() => {
  if (!searchQuery.value) return props.options;
  const query = searchQuery.value.toLowerCase();
  return props.options.filter(option => 
    option.label.toLowerCase().includes(query)
  );
});

const selectedOptionWithImage = computed(() => {
  return props?.options?.find(option => option?.value === props?.modelValue);
});

// Computed classes para aplicar estilos dinamicamente
const selectClasses = computed(() => {
  return [
    'select__select',
    props.bgClass,
    props.borderClass,
    `hover:${props.hoverBgClass}`,
    `hover:${props.hoverBorderClass}`,
    `hover:${props.hoverTextClass}`,
    `focus:${props.focusBorderClass}`
  ];
});

const dropdownClasses = computed(() => {
  return [
    'select__dropdown',
    props.dropdownBgClass,
    props.dropdownBorderClass
  ];
});

const avatarClasses = computed(() => {
  return [
    'select__value-avatar',
    props.avatarBgClass
  ];
});

const optionAvatarClasses = computed(() => {
  return [
    'select__option-avatar',
    props.avatarBgClass
  ];
});

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
      return {
        select: 'h-9 px-3 py-2 text-xs',
        option: 'h-9 px-3 py-2 text-xs',
        search: 'px-3 py-2 text-xs',
        avatar: 'w-5 h-5',
        caret: 'w-4 h-4'
      };
    case 'lg':
      return {
        select: 'h-12 px-5 py-3 text-sm',
        option: 'h-12 px-5 py-3 text-sm',
        search: 'px-5 py-3 text-sm',
        avatar: 'w-7 h-7',
        caret: 'w-5 h-5'
      };
    case 'md':
    default:
      return {
        select: 'h-11 px-4 py-2.5 text-xs md:text-sm',
        option: 'h-11 px-4 py-2.5 text-xs md:text-sm',
        search: 'px-4 py-2 text-xs md:text-sm',
        avatar: 'w-6 h-6',
        caret: 'w-5 h-5'
      };
  }
});

const handleSelect = (option) => {
  if (props.disabled) return;
  emit('update:modelValue', option.value);
  isOpen.value = false;
};

const toggleDropdown = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    searchQuery.value = '';
  }
};

const updateDropdownPosition = () => {
  if (!isOpen.value || !containerRef.value) return;
  
  const dropdown = containerRef.value.querySelector('.select__dropdown');
  if (dropdown) {
    const rect = containerRef.value.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 200;
    
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      dropdown.classList.add('select__dropdown--up');
      dropdown.classList.remove('select__dropdown--down');
      isDropdownUp.value = true;
    } else {
      dropdown.classList.remove('select__dropdown--up');
      dropdown.classList.add('select__dropdown--down');
      isDropdownUp.value = false;
    }
  }
};

watch(isOpen, (newValue) => {
  if (newValue) {
    nextTick(() => {
      updateDropdownPosition();
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', updateDropdownPosition);
    });
  } else {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', updateDropdownPosition);
  }
});

const handleScroll = () => {
  if (isOpen.value) {
    isOpen.value = false;
  }
};

onMounted(() => {
  const handleClickOutside = (event) => {
    if (containerRef.value && !containerRef.value.contains(event.target)) {
      isOpen.value = false;
    }
  };
  document.addEventListener('click', handleClickOutside);
  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', updateDropdownPosition);
  });
});

const handleClear = (event) => {
  if (props.disabled) return;
  event.stopPropagation();
  emit('update:modelValue', '');
};

const truncateValue = (value) => {
  if (props.truncated && value.length > 8) {
    return value.substring(0, 8) + '...';
  }
  return value;
};

defineExpose({ focus: () => input.value?.focus() });
</script>

<template>
  <div class="select" ref="containerRef">
    <div 
      :class="[
        ...selectClasses,
        sizeClasses.select,
        { 
          'select__select--open': isOpen,
          'select__select--has-value': modelValue,
          'select__select--disabled': disabled
        }
      ]"
      @click="toggleDropdown"
      :data-testid="dataTestId"
    >
      <div class="select__value">
        <div v-if="showProfilePicture && selectedOptionWithImage" class="select__value-with-avatar">
          <div :class="[avatarClasses, sizeClasses.avatar]">
            <img v-if="selectedOptionWithImage.profilePicture" :src="selectedOptionWithImage.profilePicture" alt="" class="select__value-avatar-img">
            <User v-else class="w-full h-full p-1 text-white" />
          </div>
          <span class="select__value-label truncate">{{ truncateValue(displayValue) }}</span>
        </div>
        <span v-else :class="{'text-white/50': !modelValue}" class="truncate">{{ displayValue }}</span>
      </div>
      <div class="select__actions">
        <button
          v-if="clearable && modelValue && !disabled"
          type="button"
          class="select__clear-button"
          @click="handleClear"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="select__clear-icon">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
          </svg>
        </button>
        <div class="select__caret">
          <svg 
            :class="[
              'select__caret-icon',
              sizeClasses.caret,
              { 'select__caret-icon--open': isOpen }
            ]"
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </div>
      </div>
    </div>

    <div v-show="isOpen && !disabled" :class="[...dropdownClasses, 'select__dropdown--down']">
      <input
        v-if="!hideSearch"
        type="text"
        :class="['select__search', sizeClasses.search]"
        v-model="searchQuery"
        placeholder="Search..."
        @click.stop
      >
      <div :class="['select__options', { 'select__options--no-search': hideSearch }]">
        <template v-if="filteredOptions?.length">
          <button
            v-for="option in filteredOptions"
            :key="option?.value"
            type="button"
            :class="['select__option', `hover:${optionHoverBgClass}`, sizeClasses.option]"
            @click="handleSelect(option)"
          >
            <div v-if="showProfilePicture" :class="[optionAvatarClasses, sizeClasses.avatar]">
              <img v-if="option?.profilePicture" :src="option?.profilePicture" alt="" class="select__option-avatar-img">
              <User v-else class="w-full h-full p-1 text-white" />
            </div>
            <span class="select__option-label truncate" data-testid="select-option-label">{{ option?.label }}</span>
          </button>
        </template>
        <div v-else class="select__no-options">
          No options available
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.select {
  @apply relative w-full;
}

.select__select {
  @apply flex items-center justify-between w-full px-4 py-2.5 rounded-[13px]
    text-white
    border
    transition-all duration-200 ease-in-out cursor-pointer
    focus:outline-none;
}

.select__select--open {
  @apply border-echoes-primary;
}

.select__select--disabled {
  @apply opacity-50 cursor-not-allowed;
}

.select__value {
  @apply flex-1 pr-2 overflow-hidden;
}

.select__value-with-avatar {
  @apply flex items-center w-full overflow-hidden;
}

.select__value-avatar {
  @apply rounded-full overflow-hidden mr-2 flex-shrink-0;
}

.select__value-avatar-img {
  @apply w-full h-full object-cover;
}

.select__value-label {
  @apply flex-1 overflow-hidden;
}

.select__placeholder {
  @apply text-white/50 hover:text-white/80 flex items-center;
}

.select__caret {
  @apply flex items-center ml-2 flex-shrink-0;
}

.select__caret-icon {
  @apply text-white/80 transition-transform duration-200;
}

.select__caret-icon--open {
  @apply transform rotate-180;
}

.select__dropdown {
  @apply absolute z-[10000]
    border
    rounded-[13px] shadow-sm
    overflow-hidden;
  width: 100%;
  max-width: 100%;
  left: 0;
}

.select__dropdown--up {
  bottom: 100%;
  margin-bottom: 0.25rem;
}

.select__dropdown--down {
  top: 100%;
  margin-top: 0.25rem;
}

.select__search {
  @apply w-full
    bg-transparent
    text-white 
    border-b border-echoes-border-gray
    placeholder-white/50 hover:placeholder-white/80;

  &:focus {
    @apply outline-none;
  }
}

.select__options {
  @apply max-h-[160px] overflow-y-auto;

  &.select__options--no-search {
    @apply border-t-0;
  }
}

.select__option {
  @apply w-full text-left
    text-white 
    transition-colors duration-150 ease-in-out
    flex items-center overflow-hidden;
}

.select__option-avatar {
  @apply rounded-full overflow-hidden mr-2 flex-shrink-0;
}

.select__option-avatar-img {
  @apply w-full h-full object-cover;
}

.select__option-label {
  @apply flex-1 overflow-hidden;
}

.select__no-options {
  @apply text-white/80;
}

.select__actions {
  @apply flex items-center gap-1 flex-shrink-0;
}

.select__clear-button {
  @apply rounded-full hover:bg-echoes-background-gray/50
    transition-colors duration-150;
}

.select__clear-icon {
  @apply w-4 h-4 text-white/80;
}
</style>

